import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Divider,
  useTheme,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  Update as UpdateIcon,
  Add as AddIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import ModalMAIQ from 'app/components/ModalMAIQ';

interface ImportSuccessModalProps {
  open: boolean;
  onClose: () => void;
  importResult: {
    success: boolean;
    importedCount: number;
    updatedCount?: number;
    failedCount: number;
    errors: any[];
    warnings: any[];
    message?: string;
  } | null;
}

const ImportSuccessModal: React.FC<ImportSuccessModalProps> = ({
  open,
  onClose,
  importResult
}) => {
  const theme = useTheme();

  // Don't render if no import result
  if (!importResult) {
    return null;
  }

  const getStatusColor = () => {
    if (importResult.success && importResult.errors.length === 0) {
      return '#2e7d32'; // Green
    } else if (importResult.success && importResult.errors.length > 0) {
      return '#ed6c02'; // Orange
    } else {
      return '#d32f2f'; // Red
    }
  };

  const getStatusIcon = () => {
    if (importResult.success && importResult.errors.length === 0) {
      return <CheckCircleIcon sx={{ color: getStatusColor(), fontSize: 48 }} />;
    } else if (importResult.success && importResult.errors.length > 0) {
      return <ErrorIcon sx={{ color: getStatusColor(), fontSize: 48 }} />;
    } else {
      return <ErrorIcon sx={{ color: getStatusColor(), fontSize: 48 }} />;
    }
  };

  const getStatusText = () => {
    if (importResult.success && importResult.errors.length === 0) {
      return 'Import Successful';
    } else if (importResult.success && importResult.errors.length > 0) {
      return 'Import Completed with Warnings';
    } else {
      return 'Import Failed';
    }
  };

  return (
    <ModalMAIQ open={open} onClose={onClose}>
      <Paper
        elevation={0}
        sx={{
          width: '90vw',
          maxWidth: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            backgroundColor: '#0F2A52',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Import Results
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {/* Status Section */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {getStatusIcon()}
            <Typography
              variant="h5"
              sx={{
                color: getStatusColor(),
                fontWeight: 600,
                mt: 2,
                mb: 1
              }}
            >
              {getStatusText()}
            </Typography>
            {importResult.message && (
              <Typography variant="body2" color="text.secondary">
                {importResult.message}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Results Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#0F2A52',
                fontWeight: 600,
                mb: 2
              }}
            >
              Import Summary
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(46, 125, 50, 0.08)',
                    color: '#2e7d32',
                    borderRadius: 1.5,
                    border: '1px solid rgba(46, 125, 50, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <UpdateIcon />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {importResult.updatedCount || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Updated Records
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={6}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976d2',
                    borderRadius: 1.5,
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <AddIcon />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {importResult.importedCount}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    New Records
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Errors Section */}
          {importResult.errors && importResult.errors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#d32f2f',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Errors ({importResult.errors.length})
              </Typography>
              
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  border: '1px solid rgba(211, 47, 47, 0.2)',
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto'
                }}
              >
                {importResult.errors.map((error, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="error">
                      {typeof error === 'string' ? error : JSON.stringify(error)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}

          {/* Success Message */}
          {importResult.success && importResult.errors.length === 0 && (
            <Box sx={{ mb: 3 }}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(46, 125, 50, 0.04)',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  borderRadius: 1,
                  textAlign: 'center'
                }}
              >
                <Typography variant="body1" color="success.main" sx={{ fontWeight: 500 }}>
                  All data has been successfully imported and updated in the system.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#0F2A52',
              color: 'white',
              '&:hover': {
                backgroundColor: '#0A1F3F',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default ImportSuccessModal;
